import { useTheme } from '@mui/material';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import { routingPath } from 'app/routes';
import { useAuthContext, usePermify } from 'modules/auth/context';
import { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  drawerActionType,
  useDrawerContext,
} from '../../context/DrawerProvider';
import { FloatingHelpButton } from '../FloatingHelpButton';
import { Header } from '../Header';
import { Celcoin, CelcoinSymbol } from '../Icons/svgs';
import {
  Container,
  Wrapper,
  DashboardContent,
  Content,
  SideMenuContent,
  ImageArea,
  IssuerModeChip,
  ButtonArea,
  SideMenuDrawerItemContent,
  SideMenuDrawerItemContentText,
} from './styles';
import { useAppContext } from 'app/context/AppContextProvider';
import { AccessManagementIcon, BoxIcon, DisbursementIcon } from '../Icons';
import { GoogleTagManager } from 'app/services/gtm';
import { EAccountPermissions } from 'modules/auth/context/permify/permify.interfaces';
import CessionIcon from '../Icons/CessionIcon';
import { ErrorBoundary } from '@sentry/react';
import { ErrorFallback } from './ErrorFallback/ErrorFallback';
import { useSnackbar } from 'app/hooks/useSnackbar';
import { useCessionService } from 'modules/cession/services/useCessionService';
import { SideMenuDrawerItem } from '../SideMenuDrawerItem';
import { SideMenuDrawer } from '../SideMenuDrawer';
import { TextL } from '../typography';
import {
  cessionActionType,
  useCessionContext,
} from 'modules/cession/context/CessionProvider';
import IdCardIcon from '../Icons/IdCardIcon';
import FileIcon from '../Icons/FileIcon';
import DashboardProviders from './DashboardProviders';

const DashboardLayout = () => {
  const { state, dispatch } = useDrawerContext();
  const { app_id, dns, logo, is_issuer_app } = useAppContext();
  const theme = useTheme();
  const navigate = useNavigate();
  const { userInfo, isLogged } = useAuthContext();
  const [hasCessionConfigurated, setHasCessionConfigurated] = useState(false);
  const { hasCessionConfiguration } = useCessionService();
  const [isSimulatorVisible, setIsSimulatorVisible] = useState(false);
  const [isSideMenuCollapsed, setIsSideMenuCollapsed] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('');
  const { pathname } = useLocation();
  const { isProfileAuthorized } = usePermify();
  const params = useParams();
  const { showSnackbarCopy } = useSnackbar();
  const { dispatch: cessionDispatch } = useCessionContext();

  useEffect(() => {
    if (!isLogged) {
      navigate(routingPath.auth.login);
    } else if (!hasCessionConfigurated) {
      hasCessionConfiguration().then((res) => {
        setHasCessionConfigurated(res);

        cessionDispatch({
          type: cessionActionType.SET_HAS_CESSION_CONFIGURATION,
          payload: {
            hasCessionConfiguration: res,
          },
        });
      });
    }
  }, [isLogged]);

  useEffect(() => {
    const newPathname = Object.keys(params).reduce((cleanPathname, param) => {
      return cleanPathname.replace(params[param] as string, `:${param}`);
    }, pathname);

    GoogleTagManager.push({
      event: 'pageview',
      page: {
        path: newPathname,
      },
    });
  }, [pathname]);

  const appLogo = useMemo(() => {
    if (logo && typeof logo.location === 'string')
      return `https://s3.sa-east-1.amazonaws.com/public.static.flowfinance.com.br/${logo.location}`;
    return isSideMenuCollapsed ? CelcoinSymbol : Celcoin;
  }, [isSideMenuCollapsed, logo]);

  const handleCloseSimulator = () => {
    setIsSimulatorVisible(false);
  };

  const handleSelectMenu = (title: string, multiple = false) => {
    if (selectedMenu === title) {
      setSelectedMenu('');
      return;
    }
    setSelectedMenu(title);
    if (multiple) {
      setIsSideMenuCollapsed(false);
    }
  };

  const handleCollapse = () => {
    setSelectedMenu('');
    setIsSideMenuCollapsed((value) => !value);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  if (!isLogged) {
    return null;
  }

  return (
    <ErrorBoundary
      fallback={ErrorFallback({
        app_id,
        dns,
        userInfo,
        theme,
        showSnackbarCopy,
      })}
      onReset={() => {
        navigate(-1);
      }}
    >
      <Container>
        <Wrapper
          activeOverlay={isSimulatorVisible}
          onClick={handleCloseSimulator}
        >
          <SideMenuDrawer
            collapsed={isSideMenuCollapsed}
            isOpen={state.isOpen}
            handleClose={() => {
              dispatch({ type: drawerActionType.CLOSE_DRAWER });
            }}
            hideOnSmallScreen
          >
            <SideMenuContent>
              <ImageArea onClick={handleCollapse}>
                <img src={appLogo} style={{ maxWidth: '170px' }} alt="logo" />
              </ImageArea>
              {is_issuer_app && !isSideMenuCollapsed && (
                <IssuerModeChip>Portal de Issuer</IssuerModeChip>
              )}
              <ButtonArea>
                <SideMenuDrawerItem
                  title="Produtos"
                  icon={
                    <BoxIcon color={theme.palette.common.black as string} />
                  }
                  hoverIcon={<BoxIcon color={theme.palette.common.white} />}
                  handleClick={() => {
                    setSelectedMenu('');
                    handleNavigate('/products/main');
                  }}
                  selected={pathname.includes('/products/')}
                  isCollapsed={isSideMenuCollapsed}
                />
                <SideMenuDrawerItem
                  title="Simulador"
                  icon={
                    <CalculateOutlinedIcon
                      style={{ color: theme.palette.common.black }}
                    />
                  }
                  hoverIcon={
                    <CalculateOutlinedIcon
                      style={{ color: theme.palette.common.white }}
                    />
                  }
                  handleClick={() => {
                    setSelectedMenu('');
                    handleNavigate(routingPath.simulation.main);
                  }}
                  selected={pathname.includes('/simulation')}
                  isCollapsed={isSideMenuCollapsed}
                />
                <SideMenuDrawerItem
                  title="Operações"
                  icon={
                    <FileIcon color={theme.palette.common.black} size={23} />
                  }
                  hoverIcon={
                    <FileIcon color={theme.palette.common.white} size={23} />
                  }
                  handleClick={() => {
                    setSelectedMenu('');
                    handleNavigate('/operations/applications');
                  }}
                  selected={pathname.includes('/operations')}
                  isCollapsed={isSideMenuCollapsed}
                />
                <SideMenuDrawerItem
                  title="Cadastros"
                  icon={
                    <IdCardIcon color={theme.palette.common.black as string} />
                  }
                  hoverIcon={
                    <IdCardIcon color={theme.palette.common.white as string} />
                  }
                  handleClick={() => handleSelectMenu('records', true)}
                  selected={
                    pathname.includes('/records/') || selectedMenu === 'records'
                  }
                  isOpen={selectedMenu === 'records'}
                  isCollapsed={isSideMenuCollapsed}
                  multiple
                >
                  <SideMenuDrawerItemContent>
                    <SideMenuDrawerItemContentText
                      isSelected={pathname.includes('/records/natural')}
                    >
                      <TextL onClick={() => handleNavigate('/records/natural')}>
                        Pessoas
                      </TextL>
                    </SideMenuDrawerItemContentText>
                    <SideMenuDrawerItemContentText
                      isSelected={pathname.includes('/records/legal')}
                    >
                      <TextL onClick={() => handleNavigate('/records/legal')}>
                        Empresas
                      </TextL>
                    </SideMenuDrawerItemContentText>
                  </SideMenuDrawerItemContent>
                </SideMenuDrawerItem>
                {isProfileAuthorized(
                  EAccountPermissions.DISBURSEMENT_ADMIN,
                ) && (
                  <SideMenuDrawerItem
                    title="Desembolsos"
                    icon={
                      <DisbursementIcon
                        color={theme.palette.common.black as string}
                      />
                    }
                    hoverIcon={
                      <DisbursementIcon
                        color={theme.palette.common.white as string}
                      />
                    }
                    handleClick={() => handleSelectMenu('disbursement', true)}
                    selected={
                      pathname.includes('/disbursement/') ||
                      selectedMenu === 'disbursement'
                    }
                    isOpen={selectedMenu === 'disbursement'}
                    isCollapsed={isSideMenuCollapsed}
                    multiple
                  >
                    <SideMenuDrawerItemContent>
                      <SideMenuDrawerItemContentText
                        isSelected={
                          pathname.match(/\/disbursement\/[^\\/]+\/balance/) !==
                          null
                        }
                      >
                        <TextL
                          onClick={() =>
                            handleNavigate(
                              `/disbursement/${userInfo?.fundings[0]}/balance`,
                            )
                          }
                        >
                          Saldo e Conciliação
                        </TextL>
                      </SideMenuDrawerItemContentText>
                    </SideMenuDrawerItemContent>
                    <SideMenuDrawerItemContent>
                      <SideMenuDrawerItemContentText
                        isSelected={
                          pathname.match(
                            /\/disbursement\/[^\\/]+\/statement/,
                          ) !== null
                        }
                      >
                        <TextL
                          onClick={() =>
                            handleNavigate(
                              `/disbursement/${userInfo?.fundings[0]}/statement`,
                            )
                          }
                        >
                          Extrato
                        </TextL>
                      </SideMenuDrawerItemContentText>
                    </SideMenuDrawerItemContent>
                  </SideMenuDrawerItem>
                )}
                <SideMenuDrawerItem
                  title="Cessão"
                  icon={
                    <CessionIcon color={theme.palette.common.black as string} />
                  }
                  hoverIcon={
                    <CessionIcon color={theme.palette.common.white as string} />
                  }
                  handleClick={() => handleSelectMenu('cession', true)}
                  selected={
                    pathname.includes('/cession/') || selectedMenu === 'cession'
                  }
                  isOpen={selectedMenu === 'cession'}
                  isCollapsed={isSideMenuCollapsed}
                  multiple
                >
                  {isProfileAuthorized(EAccountPermissions.CNAB) && (
                    <SideMenuDrawerItemContent>
                      <SideMenuDrawerItemContentText
                        isSelected={
                          pathname.match(/\/cession\/[^\\/]+\/cnab444/) !== null
                        }
                      >
                        <TextL
                          onClick={() =>
                            handleNavigate(
                              `/cession/${userInfo?.fundings[0]}/cnab444`,
                            )
                          }
                        >
                          CNAB 444
                        </TextL>
                      </SideMenuDrawerItemContentText>
                    </SideMenuDrawerItemContent>
                  )}
                  {hasCessionConfigurated && (
                    <SideMenuDrawerItemContent>
                      <SideMenuDrawerItemContentText
                        isSelected={
                          pathname.match(/\/cession\/[^\\/]+\/cessions/) !==
                          null
                        }
                      >
                        <TextL
                          onClick={() =>
                            handleNavigate(
                              `/cession/${userInfo?.fundings[0]}/cessions`,
                            )
                          }
                        >
                          Cessões
                        </TextL>
                      </SideMenuDrawerItemContentText>
                    </SideMenuDrawerItemContent>
                  )}
                </SideMenuDrawerItem>
                {isProfileAuthorized(EAccountPermissions.USER_ADMIN) && (
                  <SideMenuDrawerItem
                    title="Gestão de Acessos"
                    icon={
                      <AccessManagementIcon
                        color={theme.palette.common.black as string}
                      />
                    }
                    hoverIcon={
                      <AccessManagementIcon
                        color={theme.palette.common.white}
                      />
                    }
                    handleClick={() => {
                      setSelectedMenu('');
                      handleNavigate('/access-management');
                    }}
                    selected={pathname.includes('/access-management')}
                    isCollapsed={isSideMenuCollapsed}
                  />
                )}
              </ButtonArea>
            </SideMenuContent>
          </SideMenuDrawer>
          <Content isCollapsed={isSideMenuCollapsed}>
            <Header />
            <DashboardProviders>
              <DashboardContent>{<Outlet />}</DashboardContent>
            </DashboardProviders>
          </Content>
        </Wrapper>

        <FloatingHelpButton />
      </Container>
    </ErrorBoundary>
  );
};

export default DashboardLayout;
